import ko from "knockout";
import moment from "moment";
import ViewModel from "viewModels/ViewModel";
import apiCore from "services/apiCore";
import authService from "services/authService";
import loggingService from "services/loggingService";
import { withRequestRetry } from "util/withRequestRetry";

const SiptracesView = ViewModel.extend(function SiptracesView(
    hasCalls,
    conversationObservable
) {
    const self = this;
    self.siptraces = ko.observable();

    self.canAddPcap =
        authService.principalId() &&
        authService.hasAnyAuthorization(["perm:telephony:pcap:add"]);
    self.canViewPcap =
        authService.principalId() &&
        authService.hasAnyAuthorization(["perm:telephony:pcap:view"]);

    self.conversationId = ko.observable();
    self.dateStart = ko.observable();
    self.dateEnd = ko.observable();
    self.pcapFileUrl = ko.observable();

    // We don't want to always use a new fallback end time whenever conversationObservable is updated.
    // Instead the fallback end time will be updated whenever the conversation Id changes.
    self.fallbackEndTime = new Date();

    self.computed(() => {
        const { id, startTime, endTime } =
            ko.toJS(conversationObservable) || {};
        if (self.conversationId() !== id && !endTime) {
            self.fallbackEndTime = new Date();
        }
        self.conversationId(id);
        self.dateStart(moment(startTime).add(-1, "day").toISOString());
        self.dateEnd(
            (endTime ? moment(endTime) : moment(self.fallbackEndTime))
                .add(1, "day")
                .toISOString()
        );
    });

    const getDownloadWithRetry = function (downloadId) {
        return withRequestRetry(
            function () {
                return apiCore.v2.get(
                    "/telephony/siptraces/download/" + downloadId,
                    {
                        file: "sipDiagnostics.pcap",
                    }
                );
            },
            function (downloadResult) {
                // retry 202s -- successful requests with empty response
                loggingService.info(
                    "getDownloadWithRetry -  Retrying: " + !downloadResult
                );
                return !downloadResult;
            },
            function (error) {
                loggingService.info("getDownloadWithRetry - Error: ", error);
                return false;
            },
            { maxRetries: 5 }
        )(downloadId);
    };

    const getSiptraces = function () {
        const siptracesParams = {
            conversationId: self.conversationId(),
            dateStart: self.dateStart(),
            dateEnd: self.dateEnd(),
        };
        return apiCore.v2
            .get("/telephony/siptraces", siptracesParams)
            .then(function (siptracesResult) {
                if (siptracesResult.count > 0) {
                    self.siptraces(siptracesResult);
                    if (self.canAddPcap) {
                        return apiCore.v2
                            .post(
                                "/telephony/siptraces/download",
                                siptracesParams
                            )
                            .then(function (pcapResults) {
                                return getDownloadWithRetry(
                                    pcapResults.downloadId
                                );
                            })
                            .then(function (downloadResults) {
                                self.pcapFileUrl(downloadResults.url);
                            });
                    }
                }
            })
            .catch(function (errorResult) {
                loggingService.error(
                    "Unexpected error when fetching sip diagnostics.",
                    { errorResult }
                );
            });
    };

    const makeFileUrl = function (content, contentType) {
        const fileJSON = JSON.stringify(content, undefined, 4);
        const fileBlob = new Blob([fileJSON], { type: contentType });
        return URL.createObjectURL(fileBlob);
    };

    self.computed(function () {
        if (hasCalls() && self.dateStart()) {
            if (self.canViewPcap) {
                getSiptraces();
            }
        }
    });

    self.siptracesFileUrl = self.computed(function () {
        if (self.siptraces()) {
            return makeFileUrl(self.siptraces(), "application/json");
        }
    });
});

export default SiptracesView;
